/**
 * client/src/pages/Book.tsx — one book: while it is being imported, and after.
 *
 * This screen is two things that are really one thing. Before the edition is `ready` it is the
 * **ingest progress screen**, driven by the SSE job feed and walking parse → blocks → structure →
 * pages. After it is ready it is the book: read it, see where it begins, see what was dropped.
 *
 * They are one route because they are one object at two moments, and because the moment it flips is
 * the moment the user is looking at it. A separate `/importing` route would need a redirect, and a
 * redirect that fires while somebody is reading a progress line is a page that vanishes under them.
 *
 * §3 calls the ingest report **a product surface, not a log** — "when an ingest looks wrong the first
 * question is always *what did you throw away?*" The full report lives on `/b/:id/structure`; the
 * headline lands here the instant the import finishes, because that is when the question is asked.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { canEdit } from "../../../shared/access.ts";
import { AppFrame, BookCover, Blank, IconAlert, IconBack, IconBook, IconCheck, timeAgo } from "../App.tsx";
import { DownloadBook } from "../components/DownloadBook.tsx";
import { VoicePicker } from "../components/VoicePicker.tsx";
import { ExportSheet } from "../components/ExportSheet.tsx";
import { NarrateBook } from "../components/NarrateBook.tsx";
import { ApiError, abApi, refusalCopy } from "../lib/api.ts";
import type { BookDetail, JobRow } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { useCachedQuery } from "../lib/idb.ts";
import { INGEST_PHASES, ingestJobOf, isTerminal, phaseOf, useJobStream } from "../lib/jobs.ts";
import { StartRoom } from "../reader/StartRoom.tsx";

export function Book() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const { actor } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const detail = useCachedQuery<BookDetail>(`book:${bookId}`, () => abApi.book(bookId));
  const edition = detail.data?.edition ?? null;
  const state = edition?.state ?? null;
  const importing = detail.data !== null && (edition === null || state === "ingesting");

  // Stop listening the moment the edition is ready: an EventSource left open is a connection cloud-2
  // holds for the whole session, on a box running eleven other services.
  const stream = useJobStream(bookId, importing);
  const job = ingestJobOf(stream.jobs);

  // When the job finishes, the *edition* is what changed — ask for it again rather than inferring
  // the new state from the job's own result, which would be a second source of truth for "ready".
  const jobStatus = job?.status ?? null;
  const reload = detail.reload;
  useEffect(() => {
    if (jobStatus && (jobStatus === "done" || jobStatus === "failed")) reload();
  }, [jobStatus, reload]);

  if (detail.loading && !detail.data) return <AppFrame><Blank /></AppFrame>;

  if (!detail.data) {
    const err = detail.error;
    const missing = err instanceof ApiError && err.notFound;
    return (
      <AppFrame>
        <div className="ab-empty">
          <h2>{missing ? "No book here." : "Couldn't open that book."}</h2>
          <p>
            {missing
              ? // LAW 17: book routes refuse 404 rather than 403, precisely so this screen cannot
                // tell you whether a book exists at this id. That is the design, not a shortcoming.
                "It was deleted, or it isn't yours."
              : (err?.message ?? "Something went wrong.")}
          </p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Back to the shelf
          </Link>
        </div>
      </AppFrame>
    );
  }

  const { book, chapterCount, pageCount, narration } = detail.data;
  const mine = canEdit(book, actor);

  async function remove() {
    if (!window.confirm("Delete this book? Your place, your notes and its narration go with it.")) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await abApi.deleteBook(bookId);
      navigate("/", { replace: true });
    } catch (err) {
      setDeleting(false);
      setDeleteError(err instanceof ApiError ? err.message : "Couldn't delete it.");
    }
  }

  return (
    <AppFrame>
      <button type="button" className="ab-back" onClick={() => navigate("/")}>
        <IconBack />
        <span>Shelf</span>
      </button>

      <div className="ab-book-head">
        <div className="ab-book-cover">
          <BookCover bookId={book.id} title={book.title} author={book.author} coverSha={book.coverSha} eager />
        </div>

        <div className="ab-book-meta">
          <h1 className="ab-h1">{book.title}</h1>
          {book.author && <p className="ab-book-author">{book.author}</p>}

          <div className="ab-card-meta">
            {/* ⚠️ `publicRequestedAt` with nobody told is a column that does nothing (§6.3). The owner
                asked for public and got unlisted; if this chip said only "unlisted" they would
                reasonably conclude the request was lost. */}
            {book.publicRequestedAt !== null && book.visibility !== "public" ? (
              <span className="ab-chip ab-chip-warn">public — waiting on Karti</span>
            ) : (
              book.visibility !== "private" && <span className="ab-chip ab-chip-quiet">{book.visibility}</span>
            )}
            {state === "ready" && chapterCount > 0 && <span className="ab-chip">{chapterCount} chapters</span>}
            {state === "ready" && edition && edition.sentenceCount > 0 && (
              <span className="ab-chip">{edition.sentenceCount.toLocaleString()} sentences</span>
            )}
            {/* ⚠️ **A count against `pagesAudible`, not a percentage of `pagesTotal`.** The old chip
                divided by every page of the edition, title pages and colophons included — pages
                `planPage` refuses with `nothing-to-say`, which no GPU can ever close. Measured on the
                live shelf: *Alice* carried 15 such pages of 104, so "Narrated" was unreachable on a
                fully narrated book. The count also answers the question a percentage provokes —
                *which ones are missing* — which is now three pages rather than seven per cent. */}
            {narration && narration.pagesWithAudio > 0 && (
              <span className="ab-chip ab-chip-voice">
                {narration.pagesWithAudio >= narration.pagesAudible
                  ? "Narrated"
                  : `${narration.pagesWithAudio} of ${narration.pagesAudible} pages have a voice`}
              </span>
            )}
            <span className="ab-chip ab-chip-quiet">added {timeAgo(book.createdAt)}</span>
          </div>

          {state === "ready" && (
            <div className="ab-actions">
              <Link to={`/b/${book.id}/read`} className="ab-btn ab-btn-primary">
                <IconBook />
                <span>Read</span>
              </Link>
              {/* §7. NOT gated on `mine`: a room is the flagship interaction and the person who
                  starts one is usually not the person who uploaded the book — a public book on
                  somebody else's shelf is exactly the Tuesday-night case. `canJoinRoom` is `canSee`
                  on the server, which this screen has already satisfied by rendering at all. */}
              <StartRoom
                bookId={book.id}
                title={book.title}
                narrated={(narration?.pagesWithAudio ?? 0) > 0}
              />
              {mine && (
                <Link to={`/b/${book.id}/structure`} className="ab-btn ab-btn-ghost ab-btn-wrap">
                  Where it begins
                </Link>
              )}
              {/* P7d §3. The door to `/b/:bookId/characters`. `test/wiring.test.ts` cannot see a
                  screen nobody links to — AGENTS.md records `useRoom().createRoom` shipping with no
                  caller at all, every test green, and no way in the product to start a room. */}
              {mine && (
                <Link to={`/b/${book.id}/characters`} className="ab-btn ab-btn-ghost ab-btn-wrap">
                  Who reads it
                </Link>
              )}
              {mine && (
                <Link to={`/b/${book.id}/share`} className="ab-btn ab-btn-ghost ab-btn-wrap">
                  Share
                </Link>
              )}
            </div>
          )}

          {/* §5.8. **NOT gated on `mine`** — `canCacheOffline = canSee`, not `canEdit`. The original
              `canDownload = canEdit` would have made the entire offline story owner-only, which
              contradicts both the reader's phase plan and "anon gets all features"; and the browser
              is caching those bytes on every play regardless. `canExport` — an `.m4b`/`.epub` file to
              disk — is the one that is `canEdit`, and it is a different feature. */}
          {state === "ready" && edition && (
            <DownloadBook
              bookId={book.id}
              editionId={edition.id}
              title={book.title}
              author={book.author ?? null}
              coverSha={book.coverSha}
              narratedPages={narration?.pagesWithAudio ?? 0}
              /* Stored on the offline manifest so signing out on a shared phone drops the private
                 downloads and keeps the public ones. See `OfflineBook.visibility`. */
              visibility={book.visibility}
            />
          )}
        </div>
      </div>

      {importing && <ImportProgress job={job} live={stream.live} loaded={stream.loaded} />}

      {state === "failed" && <ImportFailed job={job} bookId={bookId} />}

      {state === "ready" && edition && (
        <ReadySummary detail={detail.data} pageCount={pageCount} mine={mine} />
      )}

      {/* P7c. Owner-or-god only, and the server enforces the same predicate — `GET /books/:id/voice`
          goes through `openBookForEdit`, so a reader who guessed the URL gets the same 404 a stranger
          does (LAW 13: the UI computes its rows from the same rule, it does not invent one). The
          picker prices the change before it is made; it never enqueues anything. */}
      {state === "ready" && edition && mine && <VoicePicker bookId={bookId} />}

      {/* P6's other half. `DownloadBook` above is the OFFLINE cache (`canCacheOffline = canSee`);
          this is the FILE on disk (`canExport = canEdit || a share link cut with downloads on`).
          Two different features with two different predicates, deliberately side by side so nobody
          re-derives one from the other. The sheet renders nothing when the server refuses — LAW 13,
          one predicate, asked rather than inferred. */}
      {state === "ready" && edition && <ExportSheet bookId={bookId} refreshKey={narration?.pagesWithAudio ?? 0} />}

      {/* P7b. §10's `POST /books/:id/narrate`, which was registered by nothing. `mine` — AGENTS.md's
          escape-hatch table says "any user, ON THEIR OWN BOOK, explicitly", and the server enforces
          the same predicate with `openBookForEdit`; the whole-book *sweep* inside it is narrower
          still (god only) and the server sends that permission rather than letting this screen infer
          it (LAW 13). The price, in nights, is on the button before it is pressed. */}
      {state === "ready" && edition && mine && <NarrateBook bookId={bookId} title={book.title} />}

      {mine && (
        <div className="ab-danger">
          <button type="button" className="ab-btn ab-btn-danger ab-btn-sm" disabled={deleting} onClick={() => void remove()}>
            {deleting ? "Deleting…" : "Delete this book"}
          </button>
          {deleteError && <span className="ab-hint ab-hint-bad">{deleteError}</span>}
        </div>
      )}
    </AppFrame>
  );
}

// ─── while it imports ────────────────────────────────────────────────────────

/**
 * The four phases, with the worker's own message under the one it is on.
 *
 * The phase comes from `jobs.message` by keyword (`lib/jobs.ts`), and an unrecognised message is
 * shown **verbatim** rather than dropped — a progress line we don't understand is still the most
 * informative thing on this screen, and silently discarding it makes a working ingest look idle.
 */
function ImportProgress({ job, live, loaded }: { job: JobRow | null; live: boolean; loaded: boolean }) {
  const [cancelling, setCancelling] = useState(false);

  // A high-water mark, not a live index. Phase detection is keyword matching over a human-written
  // message, and one ambiguous line ("writing blocks for chapter 4") must not walk the list
  // backwards — an import only moves forward, and a progress list that flickers back a step reads
  // as a restart.
  const highest = useRef(-1);
  const matched = phaseOf(job?.message ?? null);
  if (matched > highest.current) highest.current = matched;
  const active = highest.current;

  const queued = !job || job.status === "queued";

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Reading the book in</h2>
      <p className="ab-lede">
        This happens once. Sentences are split here and stored, so nothing downstream ever splits them
        again — that single fact is what keeps the highlight and the voice on the same sentence for the
        life of the book.
      </p>

      {!loaded && <p className="ab-hint">Asking the queue…</p>}

      {queued && loaded && (
        <p className="ab-note">
          <span className="ab-spinner" aria-hidden />
          <span>
            Queued. The importer runs on amd-server, next to the storage — it picks work up within a
            few seconds.
          </span>
        </p>
      )}

      <ol className="ab-phases">
        {INGEST_PHASES.map((p, i) => {
          const done = active > i || (job !== null && job.status === "done");
          const on = active === i && !done;
          return (
            <li key={p.key} className={done ? "is-done" : on ? "is-on" : ""}>
              <span className="ab-phase-mark" aria-hidden>
                {done ? <IconCheck /> : on ? <span className="ab-spinner" /> : <span className="ab-phase-dot" />}
              </span>
              <span className="ab-phase-body">
                <strong>{p.label}</strong>
                <span>{p.detail}</span>
                {on && job?.message && <em className="ab-phase-live">{job.message}</em>}
              </span>
            </li>
          );
        })}
      </ol>

      {/* The worker said something we don't have a phase for. Show it anyway — see the header:
          a progress line we can't classify is still the most informative thing on this screen. */}
      {active === -1 && job?.message && <p className="ab-phase-live">{job.message}</p>}

      {job && job.tasksTotal !== null && job.tasksDone !== null && job.tasksTotal > 0 && (
        <>
          <div className="ab-bar">
            <span className="ab-bar-fill" style={{ width: `${Math.round((job.tasksDone / job.tasksTotal) * 100)}%` }} />
          </div>
          <p className="ab-progress-label">
            {job.tasksDone.toLocaleString()} of {job.tasksTotal.toLocaleString()}
          </p>
        </>
      )}

      {job && job.attempts > 0 && job.status !== "done" && (
        <p className="ab-hint">
          Attempt {job.attempts + 1} of {job.maxAttempts}. A crashed import doesn&rsquo;t poison its own
          retry — the failed edition is cleared and the same file can be imported again.
        </p>
      )}

      <div className="ab-actions">
        {job && !isTerminal(job) && (
          <button
            type="button"
            className="ab-btn ab-btn-ghost ab-btn-sm"
            disabled={cancelling || job.status === "cancelled"}
            onClick={() => {
              setCancelling(true);
              // Cancel is a flag both sides poll (§8.2), so the worker stops at its next checkpoint
              // rather than instantly. Say "Stopping…" and mean it; the job row will land on
              // `cancelled` when it actually does.
              void abApi.cancelJob(job.id).catch(() => setCancelling(false));
            }}
          >
            {cancelling ? "Stopping…" : "Stop this import"}
          </button>
        )}
      </div>

      {job?.status === "cancelled" && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          <span>
            Stopped. The half-finished edition is cleared when its lease expires, and then the same
            file can be imported again — a cancelled import doesn&rsquo;t block its own retry.
          </span>
        </p>
      )}

      {/* Honest about the transport rather than silently degraded. If the stream is being buffered by
          a proxy — the exact failure AGENTS.md records for `encode` — this line is how anyone finds
          out, and the screen keeps working because it polls. */}
      <p className="ab-hint ab-hint-quiet">
        {live ? "Live." : "Checking every few seconds (the live stream isn't connected)."}
      </p>
    </section>
  );
}

/**
 * The importer's error arrives as a code (`scanned-pdf-unsupported`) or as a sentence, in the same
 * field. `refusalCopy` knows the codes and is the *same* table the upload screen renders through, so
 * the two can never word one refusal two ways; anything else is shown as written, because an unknown
 * refusal shown verbatim beats a generic one.
 */
function ImportFailed({ job, bookId }: { job: JobRow | null; bookId: string }) {
  const reason = job?.error ?? null;
  const copy = reason ? refusalCopy(reason) : null;
  return (
    <section className="ab-panel ab-panel-bad">
      <h2 className="ab-h2">
        <IconAlert /> This book couldn&rsquo;t be imported
      </h2>
      {copy ? (
        <>
          <p className="ab-refusal-title">{copy.title}</p>
          {copy.fix && <p>{copy.fix}</p>}
        </>
      ) : (
        <p className="ab-refusal-title">{reason ?? "The importer stopped without saying why."}</p>
      )}
      <p className="ab-hint">
        Nothing half-imported was kept. A book that <em>looks</em> readable and narrates garbage is a
        worse outcome than a refusal, so the import either lands whole or not at all.
      </p>
      {/* ⚠️ The copy here used to promise that uploading a corrected file "will be tried again",
          which reads as re-importing THIS book. It is not: `POST /books/:id/reimport` is not built,
          so the only action available mints a NEW book and leaves this one on the shelf counting
          against `AB_USER_MAX_BOOKS`. Saying so is the difference between a user who deletes the
          dead book and one who waits for a retry that is never coming.

          ⚠️ AND IT NO LONGER NAMES A PHASE. It said "lands in P3" — P3 has landed, `reimport` has
          not, and a phase number in user-facing copy goes stale in the direction nobody checks
          (AGENTS.md's own rule, and the reason it says to grep for a phase's number when it ships).
          The promise here is about the unbuilt half, and it is written so it stays true until the
          half is built. */}
      <p className="ab-hint">
        There is no way to re-import this book in place yet. A corrected file becomes a{" "}
        <em>new</em> book, so delete this one once the new one reads.
      </p>
      <Link to="/upload" className="ab-btn ab-btn-primary ab-btn-sm">
        Upload a corrected file as a new book
      </Link>
      <span className="ab-hint ab-hint-quiet">book {bookId}</span>
    </section>
  );
}

// ─── once it's ready ─────────────────────────────────────────────────────────

function ReadySummary({ detail, pageCount, mine }: { detail: BookDetail; pageCount: number; mine: boolean }) {
  const { edition } = detail;
  if (!edition) return null;

  const confidence = edition.bodyStartConfidence;
  const unconfirmed = edition.structureConfirmedAt === null;

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Imported</h2>
      <dl className="ab-facts">
        <div>
          <dt>Sentences</dt>
          <dd>{edition.sentenceCount.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Characters</dt>
          <dd>{edition.charCount.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Pages</dt>
          <dd>{pageCount.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>{edition.sourceExt.replace(/^\./, "").toUpperCase()}</dd>
        </div>
        <div>
          <dt>Language</dt>
          <dd>{edition.lang}</dd>
        </div>
        <div>
          <dt>Shape</dt>
          <dd>{edition.structure}</dd>
        </div>
      </dl>

      {edition.bodyStartReason && (
        <p className="ab-where-begins">
          <strong>Where the book begins:</strong> {edition.bodyStartReason}
          {confidence !== null && <span className="ab-confidence"> · {Math.round(confidence * 100)}% sure</span>}
        </p>
      )}

      {unconfirmed && mine && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          {/**
            * ⚠️ This used to end *"it doesn't stop a single page being narrated on demand — it stops
            * a whole book being narrated from the wrong place, which is the expensive mistake."*
            *
            * That under-promised the risk in the direction nobody checks, and the shelf proved it. On
            * four public books the ONLY narration that has ever existed was a single page, narrated
            * on demand, from the wrong place: *As a Man Thinketh*, *The Upanishads* and *The Song
            * Celestial* each spent their GPU on "This eBook is for the use of anyone anywhere in the
            * United States… Release date… Credits…", and the Bible's was 51 clips of "Genesis." "1."
            * "2." "3." — its own table of contents. 525 seconds of Karti's cloned voice, and the
            * copy on this screen said that could not happen.
            *
            * Say the true thing: confirming is also what earns the book its warm-up (LAW 10), which
            * is the difference between pressing play and waiting forty seconds.
            */}
          <span>
            Nobody has confirmed this yet. It doesn&rsquo;t stop you reading — but the first page
            anybody presses play on is narrated from wherever the importer <em>guessed</em> the book
            begins, and a wrong guess spends real time reading a copyright page aloud. Confirming
            settles that, and warms the first three pages so play is instant.
          </span>
        </p>
      )}

      {mine && (
        <Link to={`/b/${detail.book.id}/structure`} className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap">
          What was found, and what was dropped
        </Link>
      )}

      {edition.needsReview && (
        <p className="ab-hint ab-hint-bad">
          The importer flagged this edition for review — see the report.
        </p>
      )}

      {/* The edition id, on screen, deliberately: an edition is immutable and every sid, cue and
          stored position is scoped to it (LAW 1), so "which edition" is the first question worth
          asking when a place looks wrong. */}
      <p className="ab-hint ab-hint-quiet">
        edition {edition.id} · imported {timeAgo(edition.createdAt)}
      </p>
    </section>
  );
}
